import React, { useState, useEffect, useRef } from 'react';
import {
  Users,
  Brain,
  CheckCircle,
  Loader2,
  Settings,
  Clock,
  Trash2,
  ArrowRight,
} from 'lucide-react';
import './MassProbabilisticSupportingBeliefAssignment.css';
import { AdminClientApi } from '../Admin-ClientLibrary';
import {
  GetUsersInputInterface,
  GetThoughtsInputInterface,
  ClientUserInterface,
  ClientThoughtWithAuthor,
  ClientFeed,
} from '../Admin-ClientLibrary';
import { getApiUrl } from '../config/api';
import { useRecentEntities } from '../contexts/RecentEntitiesContext';
import { RecentEntity } from '../types/RecentEntities';

const MassProbabilisticSupportingBeliefAssignment: React.FC = () => {
  const { recentEntities } = useRecentEntities();
  const [step, setStep] = useState<'users' | 'thoughts' | 'ready'>('users');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [openRecentDropdown, setOpenRecentDropdown] = useState<string | null>(null);

  // User-related state
  const [goeUserId, setGoeUserId] = useState<string>('23');
  const [loeUserId, setLoeUserId] = useState<string>('144010');
  const [retrievedUsers, setRetrievedUsers] = useState<ClientUserInterface[]>([]);
  const [usersRetrieved, setUsersRetrieved] = useState(false);
  const [userRangeInfo, setUserRangeInfo] = useState<{
    count: number;
    goe: number;
    loe: number;
  } | null>(null);

  // Thought-related state
  const [thoughtSearch, setThoughtSearch] = useState('');
  const [thoughtCreatedAfter, setThoughtCreatedAfter] = useState('');
  const [thoughtCreatedBefore, setThoughtCreatedBefore] = useState('');
  const [thoughtLimit, setThoughtLimit] = useState<string>('50');
  const [isInFeed, setIsInFeed] = useState<string>('');
  const [beforeId, setBeforeId] = useState<string>('');
  const [afterId, setAfterId] = useState<string>('');
  const [retrievedThoughts, setRetrievedThoughts] = useState<ClientThoughtWithAuthor[]>([]);
  const [thoughtsRetrieved, setThoughtsRetrieved] = useState(false);
  const [thoughtSelectionMethod, setThoughtSelectionMethod] = useState<'filters' | 'feed'>(
    'filters'
  );
  const [selectedFeedId, setSelectedFeedId] = useState<string>('');
  const [probabilityMatricesRetrieved, setProbabilityMatricesRetrieved] = useState(false);
  const [probabilityMatricesData, setProbabilityMatricesData] = useState<any[]>([]);

  // Processing progress state
  const [processingProgress, setProcessingProgress] = useState<{
    currentThought: number;
    totalThoughts: number;
    currentBatch: number;
    totalBatches: number;
    totalApiCalls: number;
    completedApiCalls: number;
  } | null>(null);

  // Stop processing state
  const [stopRequested, setStopRequested] = useState(false);
  const stopRequestedRef = useRef(false);

  const client = new AdminClientApi(getApiUrl(), () => '');

  // Close recent dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.recent-dropdown-container')) {
        setOpenRecentDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Recent entities helpers
  const getFilteredRecentEntities = (
    type: 'user' | 'belief' | 'thought' | 'feed'
  ): RecentEntity[] => {
    return recentEntities.filter((entity) => entity.type === type);
  };

  const handleRecentEntityClick = (
    entity: RecentEntity,
    filterSetter: (value: string) => void
  ): void => {
    filterSetter(entity.id);
    setOpenRecentDropdown(null);
  };

  const toggleRecentDropdown = (dropdownType: string): void => {
    setOpenRecentDropdown(openRecentDropdown === dropdownType ? null : dropdownType);
  };

  const getEntityDisplayText = (entity: RecentEntity): string => {
    switch (entity.type) {
      case 'user':
        const user = entity.data as ClientUserInterface;
        return `${user.name || `User ${entity.id}`}`;
      case 'belief':
        return `Belief ${entity.id}`;
      case 'thought':
        const thought = entity.data as ClientThoughtWithAuthor;
        const text = thought.text || '';
        return text.length > 40 ? `${text.substring(0, 40)}...` : text;
      case 'feed':
        const feed = entity.data as ClientFeed;
        return feed.title || `Feed ${entity.id}`;
      default:
        return `Entity ${entity.id}`;
    }
  };

  const handleAcceptUsers = async () => {
    setLoading(true);
    setMessage('');

    try {
      // Calculate the number of users in the range instead of fetching them
      const goe = parseInt(goeUserId);
      const loe = parseInt(loeUserId);

      if (isNaN(goe) || isNaN(loe)) {
        setMessage('Error: Please enter valid user ID numbers');
        return;
      }

      if (goe > loe) {
        setMessage(
          'Error: Greater than or equal to User ID must be less than or equal to Less than or equal to User ID'
        );
        return;
      }

      const userCount = loe - goe + 1;

      // Store the range information for later processing
      setRetrievedUsers([]); // Clear any previously retrieved users
      setUsersRetrieved(true);
      setUserRangeInfo({ count: userCount, goe, loe });
      setMessage(`${userCount} users selected (IDs ${goe} to ${loe})`);
      setStep('thoughts');
    } catch (error) {
      setMessage(
        `Error calculating user range: ${error instanceof Error ? error.message : String(error)}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptThoughts = async () => {
    setLoading(true);
    setMessage('');

    try {
      let response;

      if (thoughtSelectionMethod === 'filters') {
        // Use existing filter-based method
        const input: GetThoughtsInputInterface = {
          limit: parseInt(thoughtLimit),
        };

        if (thoughtSearch) {
          input.search = thoughtSearch;
        }

        if (thoughtCreatedAfter) {
          input.createdAfter = thoughtCreatedAfter;
        }

        if (thoughtCreatedBefore) {
          input.createdBefore = thoughtCreatedBefore;
        }

        if (isInFeed) {
          input.isInFeed = parseInt(isInFeed);
        }

        if (beforeId) {
          input.beforeId = parseInt(beforeId);
        }

        if (afterId) {
          input.afterId = parseInt(afterId);
        }

        response = await client.getThoughts(input);
      } else {
        // Use new feed-based method
        if (!selectedFeedId) {
          setMessage('Error: Please enter a Feed ID');
          return;
        }

        const input = {
          feedId: parseInt(selectedFeedId),
        };

        response = await client.getRelatedThoughtsFromFeedID(input);
      }

      if (response.success) {
        setRetrievedThoughts(response.thoughts);
        setThoughtsRetrieved(true);
        setMessage(`Thoughts retrieved (${response.thoughts.length} thoughts)`);
        setStep('ready');
      } else {
        setMessage(`Error: ${response.message}`);
      }
    } catch (error) {
      setMessage(
        `Error retrieving thoughts: ${error instanceof Error ? error.message : String(error)}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleStopProcessing = () => {
    setStopRequested(true);
    stopRequestedRef.current = true;
    setMessage('Stop requested. Processing will stop after current batch completes...');
  };

  const handleStartProcessing = async () => {
    setLoading(true);
    setMessage('');
    setProbabilityMatricesRetrieved(false);
    setProbabilityMatricesData([]);
    setStopRequested(false);
    stopRequestedRef.current = false;

    try {
      if (retrievedThoughts.length === 0) {
        setMessage('Error: No thoughts available to process');
        return;
      }

      if (!userRangeInfo) {
        setMessage('Error: No user range information available');
        return;
      }

      const { goe, loe, count } = userRangeInfo;
      const batchSize = 1000;
      const totalBatches = Math.ceil(count / batchSize);
      const totalApiCalls = retrievedThoughts.length * totalBatches;

      // Initialize progress tracking
      setProcessingProgress({
        currentThought: 0,
        totalThoughts: retrievedThoughts.length,
        currentBatch: 0,
        totalBatches,
        totalApiCalls,
        completedApiCalls: 0,
      });

      let completedApiCalls = 0;
      let successfulCalls = 0;
      let failedCalls = 0;

      // Outer loop: Iterate through thoughts
      for (let thoughtIndex = 0; thoughtIndex < retrievedThoughts.length; thoughtIndex++) {
        // Check if stop was requested
        if (stopRequestedRef.current) {
          setMessage('Processing stopped by user request');
          break;
        }

        const thought = retrievedThoughts[thoughtIndex];

        // Update progress for current thought
        setProcessingProgress((prev) =>
          prev
            ? {
                ...prev,
                currentThought: thoughtIndex + 1,
                currentBatch: 0,
              }
            : null
        );

        setMessage(
          `Processing thought ${thoughtIndex + 1} of ${
            retrievedThoughts.length
          }: "${thought.text.substring(0, 50)}${thought.text.length > 50 ? '...' : ''}"`
        );

        // Inner loop: Iterate through user batches
        for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
          // Check if stop was requested
          if (stopRequestedRef.current) {
            setMessage('Processing stopped by user request');
            break;
          }
          const batchStart = goe + batchIndex * batchSize;
          const batchEnd = Math.min(batchStart + batchSize - 1, loe);

          // Update progress for current batch
          setProcessingProgress((prev) =>
            prev
              ? {
                  ...prev,
                  currentBatch: batchIndex + 1,
                  completedApiCalls,
                }
              : null
          );

          setMessage(
            `Processing thought ${thoughtIndex + 1}/${retrievedThoughts.length}, ` +
              `batch ${batchIndex + 1}/${totalBatches} (users ${batchStart}-${batchEnd})...`
          );

          try {
            // Call the new API for this thought and user batch
            const response = await client.calculateProbabilisticSupportingBeliefsForUserBatch({
              firstUserId: batchStart,
              lastUserId: batchEnd,
              thoughtId: thought.content_id,
            });

            completedApiCalls++;

            if (response.success) {
              successfulCalls++;
              console.log(
                `✅ Success: Thought ${thought.content_id}, Users ${batchStart}-${batchEnd}: ${response.message}`
              );
            } else {
              failedCalls++;
              console.warn(
                `❌ Failed: Thought ${thought.content_id}, Users ${batchStart}-${batchEnd}: ${response.message}`
              );
            }
          } catch (error) {
            completedApiCalls++;
            failedCalls++;
            console.error(
              `💥 Error: Thought ${thought.content_id}, Users ${batchStart}-${batchEnd}:`,
              error
            );
          }

          // Small delay to prevent overwhelming the server
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      }

      // Final message
      if (stopRequestedRef.current) {
        const successRate =
          totalApiCalls > 0 ? ((successfulCalls / totalApiCalls) * 100).toFixed(1) : '0';
        setMessage(
          `Processing stopped by user request. ` +
            `Completed ${completedApiCalls} of ${totalApiCalls} API calls. ` +
            `Successful: ${successfulCalls}, Failed: ${failedCalls} (${successRate}% success rate)`
        );
      } else {
        const successRate =
          totalApiCalls > 0 ? ((successfulCalls / totalApiCalls) * 100).toFixed(1) : '0';
        setMessage(
          `Processing completed! ` +
            `Processed ${retrievedThoughts.length} thoughts across ${totalBatches} user batches. ` +
            `Total API calls: ${totalApiCalls}, Successful: ${successfulCalls}, Failed: ${failedCalls} (${successRate}% success rate)`
        );
        setProbabilityMatricesRetrieved(true);
      }
    } catch (error) {
      setMessage(
        `Error during processing: ${error instanceof Error ? error.message : String(error)}`
      );
    } finally {
      setLoading(false);
      setProcessingProgress(null);
    }
  };

  const RecentDropdown: React.FC<{
    type: 'user' | 'belief' | 'thought' | 'feed';
    dropdownKey: string;
    filterSetter: (value: string) => void;
  }> = ({ type, dropdownKey, filterSetter }) => {
    const entities = getFilteredRecentEntities(type);
    const isOpen = openRecentDropdown === dropdownKey;

    if (entities.length === 0) return null;

    return (
      <div className="recent-dropdown-container">
        <button
          className="recent-dropdown-trigger"
          onClick={() => toggleRecentDropdown(dropdownKey)}
        >
          <Clock size={14} />
          Recent
        </button>
        {isOpen && (
          <div className="recent-dropdown">
            {entities.slice(0, 5).map((entity) => (
              <button
                key={entity.id}
                className="recent-dropdown-item"
                onClick={() => handleRecentEntityClick(entity, filterSetter)}
              >
                {getEntityDisplayText(entity)}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mass-probabilistic-page">
      <div className="page-header">
        <div className="header-top">
          <div className="header-title">
            <Brain size={32} />
            <h1>Mass Probabilistic Supporting Belief Assignment</h1>
          </div>
        </div>
        <p className="header-description">
          Step-by-step guide to assign supporting beliefs probabilistically to users.
        </p>
      </div>

      <div className="step-container">
        {/* Step 1: Users */}
        <div className={`step ${step === 'users' ? 'active' : ''}`}>
          <div className="step-header">
            <div className="step-number">1</div>
            <div className="step-title">
              <Users size={20} />
              <h3>Select Users</h3>
            </div>
            {usersRetrieved && <CheckCircle size={20} className="step-complete" />}
          </div>

          <div className="step-content">
            <div className="input-group">
              <label htmlFor="goeUserId">Greater than or equal to User ID:</label>
              <div className="input-with-dropdown">
                <input
                  type="number"
                  id="goeUserId"
                  value={goeUserId}
                  onChange={(e) => setGoeUserId(e.target.value)}
                  placeholder="23"
                />
                <RecentDropdown type="user" dropdownKey="goeUserId" filterSetter={setGoeUserId} />
              </div>
            </div>

            <div className="input-group">
              <label htmlFor="loeUserId">Less than or equal to User ID:</label>
              <div className="input-with-dropdown">
                <input
                  type="number"
                  id="loeUserId"
                  value={loeUserId}
                  onChange={(e) => setLoeUserId(e.target.value)}
                  placeholder="144010"
                />
                <RecentDropdown type="user" dropdownKey="loeUserId" filterSetter={setLoeUserId} />
              </div>
            </div>

            {usersRetrieved && userRangeInfo && (
              <div className="retrieved-data">
                <h4>Selected Users ({userRangeInfo.count})</h4>
                <div className="data-preview">
                  <div className="data-item">
                    <span className="user-range">
                      User IDs {userRangeInfo.goe} to {userRangeInfo.loe}
                    </span>
                    <span className="user-count">Total: {userRangeInfo.count} users</span>
                  </div>
                  <div className="data-note">
                    <p>Users will be processed in batches during the assignment step.</p>
                  </div>
                </div>
              </div>
            )}

            <button
              className="btn-primary"
              onClick={handleAcceptUsers}
              disabled={loading || !goeUserId || !loeUserId}
            >
              {loading ? <Loader2 size={16} className="spinner" /> : <ArrowRight size={16} />}
              {loading ? 'Calculating...' : 'Calculate User Range'}
            </button>
          </div>
        </div>

        {/* Step 2: Thoughts */}
        <div className={`step ${step === 'thoughts' ? 'active' : ''}`}>
          <div className="step-header">
            <div className="step-number">2</div>
            <div className="step-title">
              <Brain size={20} />
              <h3>Select Thoughts</h3>
            </div>
            {thoughtsRetrieved && <CheckCircle size={20} className="step-complete" />}
          </div>

          <div className="step-content">
            <div className="input-group">
              <label>Thought Selection Method:</label>
              <div className="radio-group">
                <label className="radio-option">
                  <input
                    type="radio"
                    name="thoughtSelectionMethod"
                    value="filters"
                    checked={thoughtSelectionMethod === 'filters'}
                    onChange={(e) =>
                      setThoughtSelectionMethod(e.target.value as 'filters' | 'feed')
                    }
                  />
                  <span>Use Filters</span>
                </label>
                <label className="radio-option">
                  <input
                    type="radio"
                    name="thoughtSelectionMethod"
                    value="feed"
                    checked={thoughtSelectionMethod === 'feed'}
                    onChange={(e) =>
                      setThoughtSelectionMethod(e.target.value as 'filters' | 'feed')
                    }
                  />
                  <span>Get from Feed</span>
                </label>
              </div>
              <div className="method-description">
                {thoughtSelectionMethod === 'filters' && (
                  <p>
                    Use filters to search and limit thoughts based on criteria like text, dates, and
                    feed membership.
                  </p>
                )}
                {thoughtSelectionMethod === 'feed' && (
                  <p>
                    Retrieve all thoughts that are related to any content in the specified feed
                    (including thoughts from beliefs in the feed).
                  </p>
                )}
              </div>
            </div>

            {thoughtSelectionMethod === 'filters' && (
              <>
                <div className="input-group">
                  <label htmlFor="thoughtSearch">Search:</label>
                  <input
                    type="text"
                    id="thoughtSearch"
                    value={thoughtSearch}
                    onChange={(e) => setThoughtSearch(e.target.value)}
                    placeholder="Search thought text..."
                  />
                </div>

                <div className="input-group">
                  <label htmlFor="thoughtCreatedAfter">Created After:</label>
                  <input
                    type="date"
                    id="thoughtCreatedAfter"
                    value={thoughtCreatedAfter}
                    onChange={(e) => setThoughtCreatedAfter(e.target.value)}
                  />
                </div>

                <div className="input-group">
                  <label htmlFor="thoughtCreatedBefore">Created Before:</label>
                  <input
                    type="date"
                    id="thoughtCreatedBefore"
                    value={thoughtCreatedBefore}
                    onChange={(e) => setThoughtCreatedBefore(e.target.value)}
                  />
                </div>

                <div className="input-group">
                  <label htmlFor="thoughtLimit">Limit:</label>
                  <input
                    type="number"
                    id="thoughtLimit"
                    value={thoughtLimit}
                    onChange={(e) => setThoughtLimit(e.target.value)}
                    min="1"
                    max="1000"
                  />
                </div>

                <div className="input-group">
                  <label htmlFor="isInFeed">In Feed (ID):</label>
                  <div className="input-with-dropdown">
                    <input
                      type="number"
                      id="isInFeed"
                      value={isInFeed}
                      onChange={(e) => setIsInFeed(e.target.value)}
                      placeholder="Optional feed ID"
                    />
                    <RecentDropdown type="feed" dropdownKey="isInFeed" filterSetter={setIsInFeed} />
                  </div>
                </div>

                <div className="input-group">
                  <label htmlFor="beforeId">Before ID (cursor-based pagination):</label>
                  <input
                    type="number"
                    id="beforeId"
                    value={beforeId}
                    onChange={(e) => setBeforeId(e.target.value)}
                    placeholder="Get thoughts with ID < this value"
                  />
                </div>

                <div className="input-group">
                  <label htmlFor="afterId">After ID (cursor-based pagination):</label>
                  <input
                    type="number"
                    id="afterId"
                    value={afterId}
                    onChange={(e) => setAfterId(e.target.value)}
                    placeholder="Get thoughts with ID > this value"
                  />
                </div>
              </>
            )}

            {thoughtSelectionMethod === 'feed' && (
              <div className="input-group">
                <label htmlFor="selectedFeedId">Feed ID:</label>
                <div className="input-with-dropdown">
                  <input
                    type="number"
                    id="selectedFeedId"
                    value={selectedFeedId}
                    onChange={(e) => setSelectedFeedId(e.target.value)}
                    placeholder="Enter feed ID"
                  />
                  <RecentDropdown
                    type="feed"
                    dropdownKey="selectedFeedId"
                    filterSetter={setSelectedFeedId}
                  />
                </div>
              </div>
            )}

            {thoughtsRetrieved && (
              <div className="retrieved-data">
                <h4>Retrieved Thoughts ({retrievedThoughts.length})</h4>
                <div className="data-preview">
                  {retrievedThoughts.slice(0, 3).map((thought) => (
                    <div key={thought.content_id} className="data-item">
                      <span className="thought-text">
                        {thought.text.length > 60
                          ? `${thought.text.substring(0, 60)}...`
                          : thought.text}
                      </span>
                    </div>
                  ))}
                  {retrievedThoughts.length > 3 && (
                    <div className="data-more">+{retrievedThoughts.length - 3} more thoughts</div>
                  )}
                </div>
              </div>
            )}

            <button
              className="btn-primary"
              onClick={handleAcceptThoughts}
              disabled={
                loading ||
                !usersRetrieved ||
                (thoughtSelectionMethod === 'feed' &&
                  (!selectedFeedId || selectedFeedId.trim() === ''))
              }
            >
              {loading ? <Loader2 size={16} className="spinner" /> : <ArrowRight size={16} />}
              {loading ? 'Retrieving Thoughts...' : 'Retrieve Thoughts'}
            </button>

            {/* Debug info - remove this later */}
            {process.env.NODE_ENV === 'development' && (
              <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
                Debug: Method={thoughtSelectionMethod}, FeedID="{selectedFeedId}", UsersRetrieved=
                {usersRetrieved.toString()}, ButtonDisabled=
                {(
                  loading ||
                  !usersRetrieved ||
                  (thoughtSelectionMethod === 'feed' &&
                    (!selectedFeedId || selectedFeedId.trim() === ''))
                ).toString()}
              </div>
            )}
          </div>
        </div>

        {/* Step 3: Ready */}
        <div className={`step ${step === 'ready' ? 'active' : ''}`}>
          <div className="step-header">
            <div className="step-number">3</div>
            <div className="step-title">
              <Settings size={20} />
              <h3>Ready to Process</h3>
            </div>
          </div>

          <div className="step-content">
            {userRangeInfo && retrievedThoughts.length > 0 && (
              <div className="processing-summary">
                <div className="summary-item">
                  <span className="summary-label">Thoughts:</span>
                  <span className="summary-value">{retrievedThoughts.length} thoughts</span>
                </div>

                <div className="summary-item">
                  <span className="summary-label">User Range:</span>
                  <span className="summary-value">
                    {userRangeInfo.goe} to {userRangeInfo.loe} ({userRangeInfo.count} users)
                  </span>
                </div>

                <div className="summary-item">
                  <span className="summary-label">User Batches:</span>
                  <span className="summary-value">
                    {Math.ceil(userRangeInfo.count / 1000)} batches (of 1000 users each)
                  </span>
                </div>

                <div className="summary-item total-batches">
                  <span className="summary-label">Total API Calls:</span>
                  <span className="summary-value">
                    {retrievedThoughts.length * Math.ceil(userRangeInfo.count / 1000)} calls
                  </span>
                </div>
              </div>
            )}

            {processingProgress && (
              <div className="processing-progress">
                <h4>Processing Progress</h4>
                <div className="progress-details">
                  <div className="progress-item">
                    <span className="progress-label">Current Thought:</span>
                    <span className="progress-value">
                      {processingProgress.currentThought} of {processingProgress.totalThoughts}
                    </span>
                  </div>
                  <div className="progress-item">
                    <span className="progress-label">Current Batch:</span>
                    <span className="progress-value">
                      {processingProgress.currentBatch} of {processingProgress.totalBatches}
                    </span>
                  </div>
                  <div className="progress-item">
                    <span className="progress-label">API Calls:</span>
                    <span className="progress-value">
                      {processingProgress.completedApiCalls} of {processingProgress.totalApiCalls}
                    </span>
                  </div>
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{
                        width: `${
                          (processingProgress.completedApiCalls /
                            processingProgress.totalApiCalls) *
                          100
                        }%`,
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            )}

            <div className="start-section">
              <div className="button-group">
                <button
                  className="btn-primary start-button"
                  onClick={handleStartProcessing}
                  disabled={!userRangeInfo || retrievedThoughts.length === 0 || loading}
                >
                  {loading ? <Loader2 size={16} className="spinner" /> : <Settings size={16} />}
                  {loading ? 'Processing...' : 'Start Processing'}
                </button>

                {loading && (
                  <button
                    className="btn-secondary stop-button"
                    onClick={handleStopProcessing}
                    disabled={stopRequestedRef.current}
                  >
                    <Trash2 size={16} />
                    {stopRequestedRef.current ? 'Stopping...' : 'Stop Processing'}
                  </button>
                )}
              </div>
            </div>

            {probabilityMatricesRetrieved && (
              <div className="processing-results">
                <h4>Processing Completed</h4>
                <div className="results-summary">
                  <div className="summary-item">
                    <span className="summary-label">Status:</span>
                    <span className="summary-value success">✅ Completed</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Processing Method:</span>
                    <span className="summary-value">Nested Loop (Thoughts × User Batches)</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">API Used:</span>
                    <span className="summary-value">
                      calculateProbabilisticSupportingBeliefsForUserBatch
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {message && (
        <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}
    </div>
  );
};

export default MassProbabilisticSupportingBeliefAssignment;
